import React, { useState } from 'react';
import { DatePicker } from '@fikasio/react-ui-components';
import { DateTime } from 'luxon';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { Task, TimeEntry } from 'services/tasks/types';
import { toIsoString } from 'utils/date';
import {
  currentRemainingTime,
  entryForSave,
  formatLoggedHours,
  hoursInputToMinutes,
  minutesBetween,
  minutesToHoursInput,
  suggestedRemainingMinutes,
  taskEstimatedRemainingMinutes,
} from './TaskPane/timeLog';

interface AddTimeEntryModalProps {
  entry?: TimeEntry;
  initialDurationMinutes?: number;
  initialTimeLeft?: string;
  onHide: () => void;
  onSave: (patch: { timeEntries: TimeEntry[] }) => void;
  show: boolean;
  task: Task;
}

const atMinute = (date: Date) => (
  DateTime.fromJSDate(date).set({ second: 0, millisecond: 0 }).toJSDate()
);

const markerDisplayTime = (entry: TimeEntry) => {
  if (entry.startTime || typeof entry.minutes === 'number' || !entry.createdAt) {
    return null;
  }
  return atMinute(new Date(entry.createdAt));
};

const startFromEntry = (entry: TimeEntry) => {
  const displayed = markerDisplayTime(entry);
  if (displayed) {
    return displayed;
  }
  const endDate = DateTime.fromJSDate(atMinute(new Date(entry.endTime)));
  if (entry.startTime) {
    return atMinute(new Date(entry.startTime));
  }
  if (entry.minutes) {
    return atMinute(endDate.minus({ minutes: entry.minutes }).toJSDate());
  }
  return endDate.toJSDate();
};

const AddTimeEntryModal = ({
  entry,
  initialDurationMinutes,
  initialTimeLeft,
  onHide,
  onSave,
  show,
  task,
}: AddTimeEntryModalProps) => {
  const { t } = useTranslation();
  const [start, setStart] = useState(() => {
    if (entry) {
      return startFromEntry(entry);
    }
    const now = DateTime.now().set({ second: 0, millisecond: 0 });
    const durationMinutes = typeof initialDurationMinutes === 'number' && initialDurationMinutes > 0
      ? initialDurationMinutes
      : 60;
    return now.minus({ minutes: durationMinutes }).toJSDate();
  });
  const [end, setEnd] = useState(() => {
    if (entry) {
      return markerDisplayTime(entry) ?? atMinute(new Date(entry.endTime));
    }
    const durationMinutes = typeof initialDurationMinutes === 'number' && initialDurationMinutes > 0
      ? initialDurationMinutes
      : 60;
    return DateTime.fromJSDate(start).plus({ minutes: durationMinutes }).toJSDate();
  });
  const [note, setNote] = useState(entry?.note ?? '');
  const [timeLeftOverride, setTimeLeftOverride] = useState<string | null>(() => {
    if (!entry && initialTimeLeft !== undefined) {
      return initialTimeLeft;
    }
    if (!entry) {
      return null;
    }
    if (typeof entry.estimatedRemainingTime === 'number') {
      return minutesToHoursInput(entry.estimatedRemainingTime);
    }
    return '';
  });
  const [invalid, setInvalid] = useState(false);

  const loggedMinutes = minutesBetween(start, end);
  const suggestedRemaining = suggestedRemainingMinutes(
    taskEstimatedRemainingMinutes(task),
    loggedMinutes,
  );
  const suggestedTimeLeft = typeof suggestedRemaining === 'number'
    ? minutesToHoursInput(suggestedRemaining)
    : '';
  const timeLeft = timeLeftOverride ?? suggestedTimeLeft;

  const save = () => {
    const startTime = toIsoString(DateTime.fromJSDate(atMinute(start)));
    let endTime = toIsoString(DateTime.fromJSDate(atMinute(end)));
    const minutes = startTime && endTime
      ? minutesBetween(new Date(startTime), new Date(endTime))
      : null;
    const currentRemaining = currentRemainingTime(task.timeEntries, task.remainingTime);
    let estimatedRemainingTime: number | null | undefined;
    if (timeLeft.trim() === '') {
      if (entry) {
        if (entry.estimatedRemainingTime !== undefined) {
          estimatedRemainingTime = null;
        }
      } else if (typeof currentRemaining === 'number') {
        estimatedRemainingTime = null;
      }
    } else {
      const remainingMinutes = hoursInputToMinutes(timeLeft, { allowZero: true });
      if (remainingMinutes == null) {
        setInvalid(true);
        return;
      }
      estimatedRemainingTime = remainingMinutes;
    }
    const keepsEstimateOnly = Boolean(
      entry && entry.minutes == null && estimatedRemainingTime !== undefined,
    );
    if (entry && keepsEstimateOnly && !minutes) {
      endTime = entry.endTime;
    }
    if (!endTime || !startTime || (!minutes && !keepsEstimateOnly)) {
      setInvalid(true);
      return;
    }
    const trimmedNote = note.trim();
    const saved: TimeEntry = {
      endTime,
      id: entry?.id ?? crypto.randomUUID(),
      ...(minutes ? { minutes, startTime } : {}),
      ...(trimmedNote ? { note: trimmedNote } : {}),
      ...(estimatedRemainingTime !== undefined ? { estimatedRemainingTime } : {}),
    };
    const existing = (task.timeEntries ?? []).map(entryForSave);
    onSave({
      timeEntries: entry
        ? existing.map(item => (item.id === entry.id ? saved : item))
        : [...existing, saved],
    });
    onHide();
  };

  return (
    <Modal
      onHide={onHide}
      show={show}
    >
      <Modal.Header closeButton>
        <Modal.Title>{entry ? t('editTimeEntry') : t('addTimeEntry')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="form-label">{t('start')}</div>
          <DatePicker
            className="form-control"
            defaultValue={start}
            displayFormat="yyyy-MM-dd HH:mm"
            onChange={date => setStart(atMinute(date))}
            showTimeSelect
            timeCaption={t('hour')}
            timeFormat="HH:mm"
            timeIntervals={15}
            value={start}
          />
        </div>
        <div className="mb-3">
          <div className="form-label">{t('end')}</div>
          <DatePicker
            className="form-control"
            defaultValue={end}
            displayFormat="yyyy-MM-dd HH:mm"
            onChange={date => setEnd(atMinute(date))}
            showTimeSelect
            timeCaption={t('hour')}
            timeFormat="HH:mm"
            timeIntervals={15}
            value={end}
          />
        </div>
        <div
          aria-invalid={invalid && loggedMinutes == null}
          className="mb-3"
        >
          <div className="form-label">{t('duration')}</div>
          <div>{loggedMinutes == null ? '—' : formatLoggedHours(loggedMinutes)}</div>
        </div>
        <div>
          <label className="form-label" htmlFor="time-entry-left">{t('timeLeft')}</label>
          <input
            aria-invalid={invalid && timeLeft.trim() !== '' && hoursInputToMinutes(timeLeft, { allowZero: true }) == null}
            className="form-control"
            id="time-entry-left"
            min="0"
            onChange={event => setTimeLeftOverride(event.target.value)}
            step="0.25"
            type="number"
            value={timeLeft}
          />
        </div>
        <div className="mb-3">
          <label className="form-label" htmlFor="time-entry-note">{t('note')}</label>
          <input
            className="form-control"
            id="time-entry-note"
            onChange={event => setNote(event.target.value)}
            type="text"
            value={note}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={onHide}
          variant="outline-secondary"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={save}
          variant="primary"
        >
          {entry ? t('save') : t('add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTimeEntryModal;
